import { createFileRoute, Link } from "@tanstack/react-router";
import { Section, SectionHeader } from "@/components/Section";
import { CTASection } from "@/components/CTASection";
import { FAQ } from "@/components/FAQ";
import { Reveal } from "@/components/Reveal";
import { OptImg } from "@/components/OptImg";
import { SITE } from "@/lib/site";

import { useSuspenseQuery } from "@tanstack/react-query";
import { listPublishedPosts } from "@/lib/content.functions";
import { formatDateTimeBR } from "@/lib/format";
import heroImg from "@/assets/gazeta-hero.jpg?format=webp&quality=72&w=1600&imagetools";
import heroImgMobile from "@/assets/gazeta-hero.jpg?format=webp&quality=70&w=800&imagetools";
import { TEAM } from "@/lib/team";
import { REVIEWS } from "@/lib/reviews";
import { CATEGORIES } from "@/lib/blog";
import { seoTitle, seoDescription } from "@/lib/seo-meta";

const TITLE = seoTitle("Gazeta Regional Online — Notícias, Reviews e Guias");
const DESC = seoDescription(
  "Webjornal com notícias atuais, reviews de produtos, guias passo a passo, tutoriais faça você mesmo e conteúdo tira-dúvidas para o leitor brasileiro.",
);

const faq = [
  { q: "O que é a Gazeta Regional Online?", a: "É um webjornal independente que publica notícias, matérias de atualidades, reviews de produtos e serviços, guias práticos, tutoriais passo a passo, conteúdo faça-você-mesmo e artigos que tiram dúvidas do leitor." },
  { q: "Qual é a linha editorial?", a: "Priorizamos apuração honesta, texto direto e leitura leve. Buscamos ajudar o leitor a entender o mundo, resolver problemas do dia a dia e tomar boas decisões de compra." },
  { q: "Como as reviews são feitas?", a: "A equipe pesquisa, compara alternativas e escreve com prós e contras reais. Quando há produtos parceiros, deixamos isso claro no artigo — o critério de recomendação continua sendo editorial." },
  { q: "Quem escreve as matérias?", a: "A redação da Gazeta é composta por jornalistas, editores e repórteres identificados na página Redação. Cada matéria é revisada antes da publicação." },
  { q: "Com que frequência publicam?", a: "Publicamos novos conteúdos todos os dias úteis. Navegue pelas editorias na página principal do jornal para acompanhar." },
];

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "/" },
    ],
    links: [
      { rel: "canonical", href: `${SITE.baseUrl}/` },
      { rel: "preload", as: "image", href: heroImgMobile, fetchPriority: "high", media: "(max-width: 768px)" },
      { rel: "preload", as: "image", href: heroImg, fetchPriority: "high", media: "(min-width: 769px)" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  loader: async ({ context }) => {
    // Prefetch para que as últimas notícias apareçam já no SSR/primeiro render.
    await context.queryClient.ensureQueryData({
      queryKey: ["public-posts"],
      queryFn: () => listPublishedPosts(),
      staleTime: 60_000,
    });
  },
  component: Home,
});

const editorialPromises = [
  { t: "Notícias apuradas", d: "Manchetes com fonte, contexto e dados. Sem clickbait, sem sensacionalismo — texto direto para leitura rápida." },
  { t: "Reviews honestos", d: "Testes de produtos e serviços com prós, contras e veredito. Comparativos que ajudam a decidir com clareza." },
  { t: "Guias passo a passo", d: "Tutoriais bem escritos, com listas, imagens e ordem lógica. Do primeiro ao último passo, sem enrolar." },
  { t: "Faça Você Mesmo", d: "Projetos práticos para tirar do papel em casa. Materiais, tempo estimado e custo médio antecipados." },
  { t: "Tira-dúvidas", d: "Respostas claras para as perguntas que o leitor faz no buscador. Sem rodeios técnicos." },
  { t: "Cobertura de atualidades", d: "Temas do momento — comportamento, economia, tecnologia — explicados de forma acessível." },
];

const why = [
  "Redação com jornalistas identificados e responsáveis por cada matéria",
  "Apuração baseada em fontes públicas, oficiais e checagem cruzada",
  "Reviews com prós e contras — nunca páginas exclusivamente promocionais",
  "Editorias organizadas por tipo de conteúdo, fácil de navegar",
  "Site leve, carregamento rápido em qualquer conexão",
  "Publicação diária com pauta focada no interesse do leitor",
];

function Home() {
  const { data: dbPosts = [] } = useSuspenseQuery({
    queryKey: ["public-posts"],
    queryFn: () => listPublishedPosts(),
    staleTime: 60_000,
  });
  const featured = dbPosts[0];
  const latestPosts = dbPosts.slice(0, 6);
  const recentPosts = dbPosts.slice(1, 5);
  const highlightCats = CATEGORIES.slice(0, 6);

  return (
    <>
      <section className="hero-bg">
        <div className="container-svg pt-10 pb-14 md:pt-16 md:pb-16">
          <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <span className="eyebrow">Edição desta semana</span>
              <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
                <span className="text-gradient">Gazeta Regional Online</span> — o webjornal do leitor brasileiro.
              </h1>
              <p className="mt-6 max-w-xl text-base text-muted-foreground md:text-lg">
                Notícias do dia, reviews de produtos, guias passo a passo, faça-você-mesmo e tira-dúvidas — publicados por uma redação independente, com apuração honesta e leitura leve.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/blog" className="btn-accent">Ler as últimas notícias</Link>
                <Link to="/sobre" className="btn-ghost">Conheça o jornal</Link>
              </div>
            </div>
            <Reveal>
              <img
                src={heroImg}
                srcSet={`${heroImgMobile} 800w, ${heroImg} 1600w`}
                sizes="(max-width: 768px) 92vw, 46vw"
                alt="Bancada da redação com jornais empilhados e uma edição aberta em destaque"
                width={1600}
                height={1024}
                fetchPriority="high"
                decoding="async"
                className="w-full rounded border-2 border-primary object-cover shadow-[var(--shadow-glow)]"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {featured && (
        <Section>
          <SectionHeader eyebrow="Destaque da capa" title="A matéria em foco agora" />
          <Link
            to="/blog/$slug"
            params={{ slug: featured.slug }}
            className="mt-10 grid gap-6 rounded border-2 border-primary bg-card p-6 md:grid-cols-[1.3fr_1fr] md:p-8 hover:shadow-[var(--shadow-glow)] transition-shadow"
          >
            {featured.cover_image && (
              <OptImg
                src={featured.cover_image}
                alt={featured.title}
                width={800}
                height={500}
                className="h-full w-full rounded object-cover"
              />
            )}
            <div className="flex flex-col justify-center">
              <span className="eyebrow">{featured.category}</span>
              <h2 className="mt-3 text-2xl font-bold leading-tight md:text-3xl">{featured.title}</h2>
              <p className="mt-3 line-clamp-4 text-muted-foreground">{featured.excerpt}</p>
              <p className="mt-4 text-xs text-muted-foreground">{featured.published_at ? formatDateTimeBR(featured.published_at) : ""}</p>
            </div>
          </Link>
        </Section>
      )}

      {latestPosts.length > 0 && (
        <Section>
          <SectionHeader
            eyebrow="Últimas notícias"
            title="As 6 matérias mais recentes"
            description="Acompanhe o que a redação da Gazeta Regional Online publicou por último."
          />
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {latestPosts.map((p) => (
              <Link
                key={p.slug}
                to="/blog/$slug"
                params={{ slug: p.slug }}
                className="card-glass block overflow-hidden p-0 transition-all hover:-translate-y-1"
              >
                {p.cover_image ? (
                  <div className="aspect-[16/10] overflow-hidden bg-secondary">
                    <OptImg src={p.cover_image} alt={p.title} width={640} height={400} className="h-full w-full object-cover" />
                  </div>
                ) : null}
                <div className="p-5">
                  <span className="eyebrow">{p.category}</span>
                  <h3 className="mt-3 text-lg font-semibold leading-snug">{p.title}</h3>
                  <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{p.excerpt}</p>
                  <p className="mt-4 text-xs text-muted-foreground">{p.published_at ? formatDateTimeBR(p.published_at) : ""}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <Link to="/blog" className="btn-accent">Ver todas as matérias</Link>
          </div>
        </Section>
      )}



      <Section>
        <SectionHeader
          eyebrow="Editorias"
          title="Navegue por tipo de conteúdo"
          description="Da notícia do dia ao tutorial passo a passo, das reviews aos artigos tira-dúvidas — tudo organizado por editoria."
        />
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {highlightCats.map((c, i) => (
            <Reveal key={c} delay={((i % 4) + 1) as 1 | 2 | 3 | 4}>
              <Link
                to="/blog"
                className="card-glass block h-full transition-all hover:-translate-y-1"
              >
                <span className="eyebrow">Editoria</span>
                <h3 className="mt-3 text-xl font-semibold">{c}</h3>
                <p className="mt-2 text-sm text-muted-foreground">Matérias, reviews e guias na editoria {c.toLowerCase()}.</p>
                <span className="mt-4 inline-block text-sm font-bold uppercase tracking-wide text-primary border-b-2 border-accent pb-0.5">Ver matérias →</span>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeader
          eyebrow="O que publicamos"
          title="Um jornal completo para o dia a dia"
          description="A Gazeta Regional Online reúne diferentes tipos de conteúdo pensados para informar, ajudar e entreter o leitor."
        />
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {editorialPromises.map((p, i) => (
            <Reveal key={p.t} delay={((i % 4) + 1) as 1 | 2 | 3 | 4}>
              <div className="card-glass h-full">
                <h3 className="text-lg font-semibold">{p.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{p.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {recentPosts.length > 0 && (
        <Section>
          <SectionHeader
            eyebrow="Últimas edições"
            title="Matérias recentes"
            description="Publicações atualizadas pela redação da Gazeta Regional Online."
          />
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {recentPosts.map((p) => (
              <Link
                key={p.slug}
                to="/blog/$slug"
                params={{ slug: p.slug }}
                className="card-glass block overflow-hidden p-0 transition-all hover:-translate-y-1"
              >
                {p.cover_image ? (
                  <div className="aspect-[16/10] overflow-hidden bg-secondary">
                    <OptImg src={p.cover_image} alt={p.title} width={640} height={400} className="h-full w-full object-cover" />
                  </div>
                ) : null}
                <div className="p-5">
                  <span className="eyebrow">{p.category}</span>
                  <h3 className="mt-3 text-lg font-semibold leading-snug">{p.title}</h3>
                  <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{p.excerpt}</p>
                  <p className="mt-4 text-xs text-muted-foreground">{p.published_at ? formatDateTimeBR(p.published_at) : ""}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <Link to="/blog" className="btn-ghost">Ver todas as matérias</Link>
          </div>
        </Section>
      )}

      <Section>
        <SectionHeader eyebrow="Por que ler a Gazeta" title="Como pensamos cada matéria" />
        <ul className="mt-10 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {why.map((w) => (
            <li key={w} className="card-glass flex items-start gap-3">
              <span aria-hidden className="mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded bg-accent text-accent-foreground text-xs font-bold">✓</span>
              <span className="text-sm text-foreground">{w}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section>
        <SectionHeader eyebrow="Redação" title="Quem escreve" />
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {TEAM.map((m, i) => (
            <Reveal key={m.name} delay={((i % 4) + 1) as 1 | 2 | 3 | 4}>
              <div className="card-glass flex h-full flex-col items-center text-center">
                {m.photo ? (
                  <img
                    src={m.photo}
                    alt={`Foto de ${m.name}`}
                    loading="lazy"
                    width={80}
                    height={80}
                    className="h-20 w-20 rounded object-cover border-2 border-primary"
                  />
                ) : (
                  <div className="flex h-20 w-20 items-center justify-center rounded text-xl font-bold text-primary bg-accent border-2 border-primary" aria-hidden>{m.initials}</div>
                )}
                <h3 className="mt-4 text-base font-semibold">{m.name}</h3>
                <p className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">{m.role}</p>
                <p className="mt-3 text-sm text-muted-foreground">{m.bio}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeader eyebrow="Cartas dos leitores" title="O que dizem os leitores" />
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {REVIEWS.slice(0, 6).map((r) => (
            <div key={r.name} className="card-glass border-l-4 border-l-accent">
              <p className="text-sm text-muted-foreground">"{r.text}"</p>
              <p className="mt-4 text-sm font-semibold">{r.name} <span className="text-xs font-normal text-muted-foreground">— {r.neighborhood}</span></p>
            </div>
          ))}
        </div>
      </Section>

      <Section>
        <FAQ items={faq} />
      </Section>

      <CTASection />
    </>
  );
}
